import { useState, useMemo } from 'react';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  SpinnerIcon,
  PlusIcon,
  UploadIcon,
  PencilIcon,
  TrashIcon,
  TagIcon,
} from '@phosphor-icons/react';
import PaginatedGentlyTable, {
  TableColumn,
} from 'src/components/common/PaginatedGentlyTable';
import {
  useProductGroupsBySupplierWithTags,
  useCreateProductGroup,
  ProductGroup,
} from 'src/api/rebates/useProductGroups';
import { useProductGroupTagMutations } from 'src/api/rebates/useProductGroupTagMutations';
import ProductGroupModal from 'src/components/rebates/ProductGroupModal';
import ProductGroupAliasesModal from 'src/components/rebates/ProductGroupAliasesModal';
import { ProductGroupDeletionDialog } from 'src/components/rebates/ProductGroupDeletionDialog';
import BulkCSVUpload from 'src/components/common/BulkCSVUpload';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import { REBATES_SYSTEM_ENUMS } from '@erp/shared-types';
import { TagsDisplay } from 'src/components/rebates/TagsDisplay';

interface SupplierProductGroupsTabProps {
  supplierId: string;
}

export function SupplierProductGroupsTab({
  supplierId,
}: SupplierProductGroupsTabProps) {
  const [productGroupModalOpen, setProductGroupModalOpen] = useState(false);
  const [editingProductGroup, setEditingProductGroup] =
    useState<ProductGroup | null>(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
  const [selectedProductGroupForDeletion, setSelectedProductGroupForDeletion] =
    useState<ProductGroup | null>(null);
  const [aliasesModalOpen, setAliasesModalOpen] = useState(false);
  const [selectedProductGroupForAliases, setSelectedProductGroupForAliases] =
    useState<ProductGroup | null>(null);

  const {
    data: productGroupsData,
    isLoading: productGroupsLoading,
    refetch: refetchProductGroups,
  } = useProductGroupsBySupplierWithTags(supplierId, !!supplierId);

  // Tag mutation hook
  const {
    handleProductGroupTagAdd,
    handleProductGroupTagRemove,
    tagsUpdatingByProductGroupId,
  } = useProductGroupTagMutations();

  // Fetch available Product Family Tags
  const { data: productFamilyTagsEnum, isLoading: tagsEnumLoading } =
    useCoreEnums({
      name: REBATES_SYSTEM_ENUMS.PRODUCT_FAMILY_TAGS.name,
      includeOptions: true,
    });

  // Get available tags from the enum
  const availableTags = useMemo(() => {
    const edges = productFamilyTagsEnum?.edges;
    if (!edges || edges.length === 0) {
      return [];
    }
    return edges[0].options || [];
  }, [productFamilyTagsEnum]);

  const createProductGroup = useCreateProductGroup();

  // Product Group handlers
  const handleCreateProductGroup = () => {
    setEditingProductGroup(null);
    setProductGroupModalOpen(true);
  };

  const handleBulkUploadProductGroups = () => {
    setBulkUploadOpen(true);
  };

  const handleEditProductGroup = (productGroup: ProductGroup) => {
    setEditingProductGroup(productGroup);
    setProductGroupModalOpen(true);
  };

  // Handle clicking the delete button - show dialog
  const handleDeleteProductGroup = (productGroup: ProductGroup) => {
    // Prevent deletion of catch-all groups
    if (productGroup.isCatchAll) {
      return;
    }

    setSelectedProductGroupForDeletion(productGroup);
    setDeletionDialogOpen(true);
  };

  // Close the deletion dialog
  const handleCloseDeletionDialog = () => {
    setDeletionDialogOpen(false);
    setSelectedProductGroupForDeletion(null);
  };

  // Handle viewing aliases for a product group
  const handleViewAliases = (productGroup: ProductGroup) => {
    setSelectedProductGroupForAliases(productGroup);
    setAliasesModalOpen(true);
  };

  // Close the aliases modal
  const handleCloseAliasesModal = () => {
    setAliasesModalOpen(false);
    setSelectedProductGroupForAliases(null);
  };

  const handleProductGroupModalSuccess = () => {
    refetchProductGroups();
  };

  // Expected CSV columns for bulk product group upload
  const expectedColumns = ['name', 'description'];

  // Handle individual row upload for bulk CSV
  const handleUploadProductGroupRow = async (
    rowData: Record<string, unknown>,
    index: number,
  ): Promise<void> => {
    try {
      // Transform CSV row data to match API format
      const productGroupData = {
        supplierId: supplierId,
        name: String(rowData.name),
        description: String(rowData.description || ''),
      };

      // Call the API to create product group
      await createProductGroup.mutateAsync(productGroupData);

      console.log(
        `Successfully uploaded product group ${index + 1}:`,
        productGroupData.name,
      );
    } catch (error) {
      console.error(`Failed to upload product group ${index + 1}:`, error);
      throw error; // Re-throw to trigger failed status
    }
  };

  // Handle completion of bulk upload
  const handleBulkUploadComplete = (results: Array<{ status: string }>) => {
    const successful = results.filter((r) => r.status === 'success').length;
    const failed = results.filter((r) => r.status === 'failed').length;

    console.log(
      `Bulk upload completed: ${successful} successful, ${failed} failed`,
    );

    // Refresh the data to show new product groups
    refetchProductGroups();

    // Close the modal
    setBulkUploadOpen(false);
  };

  // Check if any product groups have enableProductCodes in metadata
  const hasEnableProductCodes = (productGroupsData || []).some(
    (pg) =>
      pg.metadata?.enableProductCodes &&
      pg.metadata.enableProductCodes.length > 0,
  );

  // Product Group table columns
  const productGroupColumns: TableColumn<ProductGroup>[] = [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate" title={value}>
            {value}
          </span>
          {row.isCatchAll && (
            <Badge variant="secondary" className="text-xs shrink-0">
              Default
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: 'description',
      label: 'Description',
      sortable: true,
      render: (value) => (
        <span className="text-sm block" style={{ wordBreak: 'break-word' }}>
          {value || '-'}
        </span>
      ),
    },
    ...(hasEnableProductCodes
      ? [
          {
            id: 'internalCodes' as keyof ProductGroup,
            label: 'Enable Codes',
            sortable: false,
            render: (_value: unknown, row: ProductGroup) => {
              const codes = row.metadata?.enableProductCodes;
              if (!codes || codes.length === 0) {
                return <span className="text-muted-foreground">-</span>;
              }
              return (
                <div className="flex flex-wrap gap-1">
                  {codes.map((code) => (
                    <Badge key={code} variant="outline" className="text-xs">
                      {code}
                    </Badge>
                  ))}
                </div>
              );
            },
          },
        ]
      : []),
    {
      id: 'tags' as keyof ProductGroup,
      label: 'Product Family Tags',
      sortable: false,
      render: (_value, row) => {
        // Convert ProductGroup tags to TagAssociation format expected by TagsDisplay
        const currentTags = (row.tags || []).map((tag) => ({
          id: tag.id,
          coreEnumOptionId: tag.id,
          coreEnumOption: {
            id: tag.id,
            name: tag.name,
            description: tag.description,
            sortOrder: 0,
            enumId: '',
          },
        }));
        const currentTagIdArray = currentTags.map((t) => t.coreEnumOptionId);
        const currentTagIds = new Set(currentTagIdArray);
        const isUpdating = tagsUpdatingByProductGroupId.get(row.id) ?? false;

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <TagsDisplay
              currentTags={currentTags}
              availableTags={availableTags}
              currentTagIds={currentTagIds}
              isLoading={tagsEnumLoading}
              isUpdating={isUpdating}
              maxTags={1}
              onAddTag={(tagId) =>
                handleProductGroupTagAdd(row.id, tagId, currentTagIdArray)
              }
              onRemoveTag={(tagId) =>
                handleProductGroupTagRemove(row.id, tagId, currentTagIdArray)
              }
              layout="compact"
              showEmptyState={false}
            />
          </div>
        );
      },
    },
    {
      id: 'isHistorical',
      label: 'Historical',
      sortable: true,
      render: (value) => (
        <span className="text-sm whitespace-nowrap">
          {value ? 'Historical' : '-'}
        </span>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (value) => (
        <span className="text-sm whitespace-nowrap">
          {new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          }).format(new Date(value as string))}
        </span>
      ),
    },
    {
      id: 'actions' as keyof ProductGroup,
      label: 'Actions',
      align: 'center' as const,
      render: (_, row) => (
        <div className="flex gap-1 justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewAliases(row);
                  }}
                  className="h-8 w-8 p-0"
                >
                  <TagIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Manage aliases</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditProductGroup(row);
                  }}
                  className="h-8 w-8 p-0"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Edit product group</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {!row.isCatchAll && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProductGroup(row);
                    }}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete product group</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="p-6 space-y-8">
        <div>
          <h2 className="text-xl font-medium mb-6">Rebate Product Groups</h2>

          {productGroupsLoading ? (
            <div className="flex justify-center p-6">
              <SpinnerIcon className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <PaginatedGentlyTable
                data={productGroupsData || []}
                columns={productGroupColumns}
                searchFields={['name', 'description']}
                searchPlaceholder="Search product groups..."
                emptyMessage="No product groups found for this supplier"
                isLoading={productGroupsLoading}
                actions={
                  <div className="flex gap-2">
                    <Button
                      onClick={handleBulkUploadProductGroups}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <UploadIcon className="h-4 w-4" />
                      Bulk Upload
                    </Button>
                    <Button
                      onClick={handleCreateProductGroup}
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Create Product Group
                    </Button>
                  </div>
                }
                defaultSort={{
                  sortBy: 'name',
                  sortDirection: 'asc',
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Product Group Modal */}
      <ProductGroupModal
        open={productGroupModalOpen}
        onClose={() => setProductGroupModalOpen(false)}
        supplierId={supplierId}
        productGroup={editingProductGroup || undefined}
        onSuccess={handleProductGroupModalSuccess}
      />

      {/* Product Group Deletion Dialog */}
      <ProductGroupDeletionDialog
        open={deletionDialogOpen}
        onClose={handleCloseDeletionDialog}
        productGroup={selectedProductGroupForDeletion}
        onDeleted={refetchProductGroups}
      />

      {/* Bulk Upload Modal */}
      <Dialog
        open={bulkUploadOpen}
        onOpenChange={() => setBulkUploadOpen(false)}
      >
        <DialogContent className="max-w-[95vw] w-[95vw] sm:max-w-[95vw] md:max-w-[95vw] lg:max-w-[95vw] xl:max-w-[95vw] max-h-[90vh] h-auto flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Bulk Product Group Upload</DialogTitle>
            <DialogDescription>
              Upload a CSV file containing product group information. Each row
              will be processed as a new product group for this supplier.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            <BulkCSVUpload
              title=""
              description=""
              expectedColumns={expectedColumns}
              onUploadRow={handleUploadProductGroupRow}
              onComplete={handleBulkUploadComplete}
              maxRows={500}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Group Aliases Modal */}
      <ProductGroupAliasesModal
        open={aliasesModalOpen}
        onClose={handleCloseAliasesModal}
        productGroup={selectedProductGroupForAliases}
      />
    </>
  );
}
